import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';

import useTranslate from '../../hooks/useTranslate';
import Text from '../Text/Text';

interface ExpandableTextPropsType {
  text: ReactNode;
  clamp: number;
  buttonPosition?: 'right' | 'inline';
}

const ExpandableText: FC<ExpandableTextPropsType> = ({ text, clamp, buttonPosition = 'right' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const { translate } = useTranslate();

  const measureTextFullHeight = useCallback(() => {
    if (textRef.current) {
      const container = textRef.current.cloneNode(true) as HTMLDivElement;
      Object.assign(container.style, {
        position: 'absolute',
        visibility: 'hidden',
        height: 'auto',
        maxHeight: 'none',
        overflow: 'visible',
        width: `${textRef.current.offsetWidth}px`,
      });

      document.body.appendChild(container);

      const span = container.querySelector('span');
      if (span) {
        span.style.webkitLineClamp = 'unset';
        const fullHeight = span.scrollHeight;
        const lineHeight = parseFloat(getComputedStyle(span).lineHeight || '0');
        const clampedHeight = Math.ceil(lineHeight * clamp);
        setShowButton(fullHeight > clampedHeight);
      }

      document.body.removeChild(container);
    }
  }, [clamp]);

  useEffect(() => {
    measureTextFullHeight();
  }, [text, clamp, measureTextFullHeight]);

  const ExpandButton = () => (
    <Button
      color="link"
      className="px-0 fw-bold"
      style={{ color: '#283646', textDecoration: 'underline', fontSize: '14px' }}
      onClick={() => setIsExpanded(true)}
    >
      {translate('golfClubPage.expandDescriptionCTA')}
    </Button>
  );

  return (
    <div>
      {buttonPosition === 'inline' ? (
        <div ref={textRef}>
          <Text
            clamp={isExpanded ? undefined : clamp}
            style={isExpanded ? {} : { cursor: 'pointer' }}
            onClick={() => setIsExpanded(true)}
          >
            {text}
          </Text>
          {!isExpanded && showButton && <ExpandButton />}
        </div>
      ) : (
        <>
          <div ref={textRef}>
            <Text clamp={isExpanded ? undefined : clamp}>{text}</Text>
          </div>
          {!isExpanded && showButton && (
            <div className="text-end">
              <ExpandButton />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExpandableText;
